import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { api, ApiError } from '@/lib/api';
import type { SiteBundle } from '@/types/api';

interface SiteContextValue extends SiteBundle {
  preview: boolean;
}

const SiteContext = createContext<SiteContextValue | null>(null);

export function isPreviewRequested(search = window.location.search) {
  return new URLSearchParams(search).get('preview') === '1';
}

async function fetchBundle(preview: boolean): Promise<SiteBundle & { preview: boolean }> {
  if (preview) {
    try {
      // Draft content is only available to signed-in administrators with content permission.
      return { ...(await api<SiteBundle>('/admin/content/preview')), preview: true };
    } catch (err) {
      if (!(err instanceof ApiError) || (err.status !== 401 && err.status !== 403)) throw err;
    }
  }
  return { ...(await api<SiteBundle>('/site-content')), preview: false };
}

export function useSiteBundleQuery(preview: boolean) {
  return useQuery({
    queryKey: ['site-content', preview],
    queryFn: () => fetchBundle(preview),
    staleTime: preview ? 0 : 5 * 60_000,
  });
}

export function SiteProvider({ bundle, children }: { bundle: SiteBundle & { preview: boolean }; children: ReactNode }) {
  const value = useMemo(() => bundle, [bundle]);
  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export function useSite(): SiteContextValue {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error('useSite must be used inside <SiteProvider>');
  return ctx;
}
